'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowUp, Square, X } from 'lucide-react';
import { type CSSProperties, type Ref, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { ModelPicker } from '@/components/ui/model-picker';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { ChatModelOption } from '@/types/chat';
interface ChatInputProperties {
  disabled?: boolean | undefined;
  inputRef?: Ref<HTMLTextAreaElement> | null | undefined;
  isLoading?: boolean | undefined;
  isModelOptionsLoading?: boolean | undefined;
  layout?: 'floating' | 'sidebar' | undefined;
  modelOptions: ChatModelOption[];
  modelOptionsError?: string | null | undefined;
  modelPickerEnabled?: boolean | undefined;
  onChange: (value: string) => void;
  onModelChange: (modelId: string) => void;
  onRemoveQueuedMessage?: ((id: string) => void) | undefined;
  onStartFresh?: (() => void) | undefined;
  onStop?: (() => void) | undefined;
  onSubmit: (value: string) => void;
  placeholder?: string | undefined;
  queuedMessages?: Array<{
    id: string;
    text: string;
  }> | undefined;
  selectedModelId: string | null;
  value: string;
}
export function ChatInput(t0) {
  const $ = _c(108);
  const {
    value,
    onChange,
    onSubmit,
    disabled,
    onStop,
    isLoading,
    modelOptions,
    selectedModelId,
    onModelChange,
    isModelOptionsLoading: t1,
    modelOptionsError,
    onStartFresh,
    queuedMessages: t2,
    layout: t3,
    onRemoveQueuedMessage,
    placeholder,
    modelPickerEnabled: t4,
    inputRef: t5
  } = t0;
  const isModelOptionsLoading = t1 === undefined ? false : t1;
  let t6;
  if ($[0] !== t2) {
    t6 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const queuedMessages = t6;
  const layout = t3 === undefined ? "floating" : t3;
  const modelPickerEnabled = t4 === undefined ? true : t4;
  const inputRef = t5 === undefined ? null : t5;
  const isSidebar = layout === "sidebar";
  let t7;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      backdropFilter: "blur(42px)",
      WebkitBackdropFilter: "blur(42px)"
    };
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const glassLayerStyle = t7;
  let t8;
  if ($[3] !== disabled || $[4] !== onSubmit || $[5] !== value) {
    t8 = () => {
      if (value.trim() && !disabled) {
        onSubmit(value);
      }
    };
    $[3] = disabled;
    $[4] = onSubmit;
    $[5] = value;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const handleSubmit = t8;
  let t9;
  if ($[7] !== handleSubmit) {
    t9 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    };
    $[7] = handleSubmit;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const handleKeyDown = t9;
  const showModelSelector = modelPickerEnabled && (isModelOptionsLoading || !!modelOptionsError || modelOptions.length > 0 || selectedModelId !== null);
  const placeholderText = isModelOptionsLoading ? "Loading models\u2026" : "Select a model";
  const composerPlaceholder = placeholder ?? "Ask about your timeline...";
  if (isSidebar) {
    let t10;
    if ($[9] !== onRemoveQueuedMessage || $[10] !== queuedMessages) {
      t10 = queuedMessages.length > 0 ? <div className="flex flex-col gap-2">{queuedMessages.map(queued => <div key={queued.id} className="flex items-center justify-between gap-2 rounded-2xl border border-border/40 bg-muted/30 px-3 py-1.5 text-[12px] text-muted-foreground shadow-inner shadow-black/5 dark:border-white/15 dark:bg-black/40 dark:text-white/70 dark:shadow-black/10"><span className="line-clamp-1 font-medium text-foreground dark:text-white/90">{queued.text}</span><div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70 dark:text-white/50">Queued</span>{onRemoveQueuedMessage ? <Button type="button" size="icon" variant="ghost" onClick={() => {
              onRemoveQueuedMessage(queued.id);
            }} aria-label="Remove queued message" className="h-5 w-5 rounded-full border border-border/40 bg-card/80 p-0 text-muted-foreground transition hover:bg-muted/40 hover:text-foreground dark:border-white/15 dark:bg-black/40 dark:text-white/70 dark:hover:bg-black/50 dark:hover:text-white"><X className="h-3 w-3" /></Button> : null}</div></div>)}</div> : null;
      $[9] = onRemoveQueuedMessage;
      $[10] = queuedMessages;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    const queuedList = t10;
    let t11;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = cn("relative flex flex-col overflow-hidden rounded-[24px] border border-border/60 bg-card/95 px-2.5 pb-2 pt-2 shadow-[032px_70px_-34px_rgba(6,10,18,0.12)] transition-shadow before:pointer-events-none before:absolute before:inset-0 before:rounded-[inherit] before:bg-gradient-to-br before:from-white/40 before:via-white/20 before:to-transparent before:opacity-50 before:content-['']", "dark:border-white/18 dark:bg-black/35 dark:shadow-[032px_70px_-34px_rgba(0,0,0,0.9)] dark:before:from-white/6 dark:before:via-white/3 dark:before:opacity-90", "supports-[backdrop-filter]:border-border/50 supports-[backdrop-filter]:bg-card/90", "dark:supports-[backdrop-filter]:border-white/20 dark:supports-[backdrop-filter]:bg-black/40 dark:supports-[backdrop-filter]:shadow-[044px_96px_-46px_rgba(0,0,0,0.95)]");
      $[12] = t11;
    } else {
      t11 = $[12];
    }
    let t12;
    if ($[13] !== onChange) {
      t12 = e_0 => {
        onChange(e_0.target.value);
      };
      $[13] = onChange;
      $[14] = t12;
    } else {
      t12 = $[14];
    }
    const t13 = disabled && "opacity-60";
    let t14;
    if ($[15] !== t13) {
      t14 = cn("min-h-[72px] max-h-[320px] w-full resize-none border-0 bg-transparent px-1 py-1 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:ring-transparent", "dark:text-white dark:placeholder:text-white/40", t13);
      $[15] = t13;
      $[16] = t14;
    } else {
      t14 = $[16];
    }
    let t15;
    if ($[17] !== composerPlaceholder || $[18] !== disabled || $[19] !== handleKeyDown || $[20] !== inputRef || $[21] !== t12 || $[22] !== t14 || $[23] !== value) {
      t15 = <Textarea ref={inputRef} value={value} onChange={t12} onKeyDown={handleKeyDown} disabled={disabled} placeholder={composerPlaceholder} rows={1} className={t14} />;
      $[17] = composerPlaceholder;
      $[18] = disabled;
      $[19] = handleKeyDown;
      $[20] = inputRef;
      $[21] = t12;
      $[22] = t14;
      $[23] = value;
      $[24] = t15;
    } else {
      t15 = $[24];
    }
    let t16;
    if ($[25] !== disabled || $[26] !== isModelOptionsLoading || $[27] !== modelOptions || $[28] !== modelOptionsError || $[29] !== onModelChange || $[30] !== placeholderText || $[31] !== selectedModelId || $[32] !== showModelSelector) {
      t16 = showModelSelector ? <ModelPicker options={modelOptions} value={selectedModelId} onChange={onModelChange} disabled={Boolean(disabled)} placeholder={placeholderText} isLoading={isModelOptionsLoading} error={modelOptionsError} emptyMessage="No alternative models available." className="w-full max-w-[min(320px,90vw)] shrink-0" triggerClassName="h-8 rounded-full border border-border/50 bg-transparent px-3 text-xs font-medium text-foreground shadow-none hover:bg-muted/30 dark:border-white/15 dark:text-white/90 dark:hover:bg-black/20" showProvider={false} showProviderLogo={false} getDisplayLabel={_temp} dropdownProps={{
        showProvider: true,
        showProviderLogo: true
      }} /> : null;
      $[25] = disabled;
      $[26] = isModelOptionsLoading;
      $[27] = modelOptions;
      $[28] = modelOptionsError;
      $[29] = onModelChange;
      $[30] = placeholderText;
      $[31] = selectedModelId;
      $[32] = showModelSelector;
      $[33] = t16;
    } else {
      t16 = $[33];
    }
    let t17;
    if ($[34] !== t16) {
      t17 = <div className="flex min-w-0 flex-1 items-center">{t16}</div>;
      $[34] = t16;
      $[35] = t17;
    } else {
      t17 = $[35];
    }
    let t18;
    if ($[36] !== disabled || $[37] !== isLoading || $[38] !== onStartFresh) {
      t18 = onStartFresh ? <Button type="button" size="sm" variant="ghost" onClick={onStartFresh} disabled={disabled || isLoading} className="h-8 rounded-full px-3 text-xs font-semibold text-muted-foreground hover:bg-muted/30 hover:text-foreground dark:text-white/70 dark:hover:bg-black/40 dark:hover:text-white">Clear</Button> : null;
      $[36] = disabled;
      $[37] = isLoading;
      $[38] = onStartFresh;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    let t19;
    if ($[40] !== disabled || $[41] !== handleSubmit || $[42] !== isLoading || $[43] !== onStop || $[44] !== value) {
      t19 = isLoading && onStop ? <Button onClick={onStop} size="icon" variant="secondary" className="h-9 w-9 rounded-full border border-border/50 bg-muted/40 p-0 text-muted-foreground shadow-inner shadow-black/5 hover:bg-muted/60 dark:border-white/15 dark:bg-black/40 dark:text-white/70 dark:shadow-black/20 dark:hover:bg-black/50"><Square className="h-4 w-4" /></Button> : <Button onClick={handleSubmit} disabled={disabled || !value.trim()} size="icon" className={cn("h-9 w-9 rounded-full bg-primary text-primary-foreground shadow-sm transition hover:bg-primary/90", (disabled || !value.trim()) && "opacity-40")}><ArrowUp className="h-4 w-4" /></Button>;
      $[40] = disabled;
      $[41] = handleSubmit;
      $[42] = isLoading;
      $[43] = onStop;
      $[44] = value;
      $[45] = t19;
    } else {
      t19 = $[45];
    }
    let t20;
    if ($[46] !== t18 || $[47] !== t19) {
      t20 = <div className="flex items-center gap-1.5">{t18}{t19}</div>;
      $[46] = t18;
      $[47] = t19;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] !== t17 || $[50] !== t20) {
      t21 = <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-border/40 pt-1.5 dark:border-white/10 supports-[backdrop-filter]:border-border/35 dark:supports-[backdrop-filter]:border-white/15">{t17}{t20}</div>;
      $[49] = t17;
      $[50] = t20;
      $[51] = t21;
    } else {
      t21 = $[51];
    }
    let t22;
    if ($[52] !== t15 || $[53] !== t21) {
      t22 = <div className={t11} style={glassLayerStyle}>{t15}{t21}</div>;
      $[52] = t15;
      $[53] = t21;
      $[54] = t22;
    } else {
      t22 = $[54];
    }
    let t23;
    if ($[55] !== queuedList || $[56] !== t22) {
      t23 = <div className="w-full"><div className="flex flex-col gap-2 pb-1 pt-1 text-card-foreground">{queuedList}{t22}</div></div>;
      $[55] = queuedList;
      $[56] = t22;
      $[57] = t23;
    } else {
      t23 = $[57];
    }
    return t23;
  }
  let t10;
  if ($[58] !== onRemoveQueuedMessage || $[59] !== queuedMessages) {
    t10 = queuedMessages.length > 0 ? <div className="flex flex-col gap-2">{queuedMessages.map(queued_0 => <div key={queued_0.id} className="flex items-center justify-between gap-3 rounded-2xl border border-border/30 bg-muted/30 px-4 py-2 text-xs text-muted-foreground shadow-inner shadow-black/5"><span className="line-clamp-1 font-medium text-card-foreground">{queued_0.text}</span><div className="flex items-center gap-1.5"><span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">Queued</span>{onRemoveQueuedMessage ? <Button type="button" size="icon" variant="ghost" onClick={() => {
            onRemoveQueuedMessage(queued_0.id);
          }} aria-label="Remove queued message" className="h-6 w-6 rounded-full border border-border/40 bg-card/80 p-0 text-muted-foreground transition hover:bg-muted/30 hover:text-card-foreground dark:bg-card/70"><X className="h-3.5 w-3.5" /></Button> : null}</div></div>)}</div> : null;
    $[58] = onRemoveQueuedMessage;
    $[59] = queuedMessages;
    $[60] = t10;
  } else {
    t10 = $[60];
  }
  let t11;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = cn("pointer-events-none whitespace-pre-wrap break-words px-4 py-3 text-sm leading-relaxed opacity-0", "min-h-[72px] max-h-[320px] overflow-hidden");
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  const t12 = value.length > 0 ? value : " ";
  let t13;
  if ($[62] !== t12) {
    t13 = <div aria-hidden="true" className={t11}>{t12}{"\n"}</div>;
    $[62] = t12;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== onChange) {
    t14 = e_1 => {
      onChange(e_1.target.value);
    };
    $[64] = onChange;
    $[65] = t14;
  } else {
    t14 = $[65];
  }
  const t15 = disabled && "opacity-60";
  let t16;
  if ($[66] !== t15) {
    t16 = cn("absolute inset-0 h-full w-full resize-none rounded-2xl border border-border/35 bg-card/80 px-4 py-4 text-sm leading-relaxed shadow-inner shadow-black/10 backdrop-blur supports-[backdrop-filter]:bg-background/45 supports-[backdrop-filter]:border-border/25 focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0", "min-h-[72px] max-h-[320px] overflow-hidden", t15);
    $[66] = t15;
    $[67] = t16;
  } else {
    t16 = $[67];
  }
  let t17;
  if ($[68] !== composerPlaceholder || $[69] !== disabled || $[70] !== handleKeyDown || $[71] !== inputRef || $[72] !== t14 || $[73] !== t16 || $[74] !== value) {
    t17 = <Textarea value={value} onChange={t14} onKeyDown={handleKeyDown} placeholder={composerPlaceholder} disabled={disabled} ref={inputRef} className={t16} rows={1} />;
    $[68] = composerPlaceholder;
    $[69] = disabled;
    $[70] = handleKeyDown;
    $[71] = inputRef;
    $[72] = t14;
    $[73] = t16;
    $[74] = value;
    $[75] = t17;
  } else {
    t17 = $[75];
  }
  let t18;
  if ($[76] !== t13 || $[77] !== t17) {
    t18 = <div className="relative">{t13}{t17}</div>;
    $[76] = t13;
    $[77] = t17;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== disabled || $[80] !== isModelOptionsLoading || $[81] !== modelOptions || $[82] !== modelOptionsError || $[83] !== onModelChange || $[84] !== placeholderText || $[85] !== selectedModelId || $[86] !== showModelSelector) {
    t19 = showModelSelector ? <ModelPicker options={modelOptions} value={selectedModelId} onChange={onModelChange} disabled={Boolean(disabled)} placeholder={placeholderText} isLoading={isModelOptionsLoading} error={modelOptionsError} emptyMessage="No alternative models available." className="w-full sm:w-auto sm:max-w-[240px]" triggerClassName="h-11 rounded-full border border-border/30 bg-muted/30 px-4 text-sm font-medium shadow-none" dropdownProps={{
      showProvider: true,
      showProviderLogo: true
    }} /> : null;
    $[79] = disabled;
    $[80] = isModelOptionsLoading;
    $[81] = modelOptions;
    $[82] = modelOptionsError;
    $[83] = onModelChange;
    $[84] = placeholderText;
    $[85] = selectedModelId;
    $[86] = showModelSelector;
    $[87] = t19;
  } else {
    t19 = $[87];
  }
  let t20;
  if ($[88] !== disabled || $[89] !== isLoading || $[90] !== onStartFresh) {
    t20 = onStartFresh ? <Button type="button" size="sm" variant="outline" onClick={onStartFresh} disabled={disabled || isLoading} className="h-11 rounded-full border-border/40 px-4 text-sm font-semibold">Clear</Button> : null;
    $[88] = disabled;
    $[89] = isLoading;
    $[90] = onStartFresh;
    $[91] = t20;
  } else {
    t20 = $[91];
  }
  let t21;
  if ($[92] !== t19 || $[93] !== t20) {
    t21 = <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">{t19}{t20}</div>;
    $[92] = t19;
    $[93] = t20;
    $[94] = t21;
  } else {
    t21 = $[94];
  }
  let t22;
  if ($[95] !== disabled || $[96] !== handleSubmit || $[97] !== isLoading || $[98] !== onStop || $[99] !== value) {
    t22 = <div className="flex items-center gap-1.5">{isLoading && onStop ? <Button onClick={onStop} size="icon" variant="secondary" className="h-12 w-12 rounded-full border border-border/50 bg-muted/50 p-0 text-muted-foreground shadow-inner shadow-black/10"><Square className="h-4 w-4" /></Button> : <Button onClick={handleSubmit} disabled={disabled || !value.trim()} size="icon" className={cn("h-12 w-12 rounded-full bg-primary text-primary-foreground shadow-lg transition hover:bg-primary/90 dark:shadow-[018px_48px_-24px_rgba(0,0,0,0.65)]", (disabled || !value.trim()) && "opacity-60")}><ArrowUp className="h-4 w-4" /></Button>}</div>;
    $[95] = disabled;
    $[96] = handleSubmit;
    $[97] = isLoading;
    $[98] = onStop;
    $[99] = value;
    $[100] = t22;
  } else {
    t22 = $[100];
  }
  let t23;
  if ($[101] !== t21 || $[102] !== t22) {
    t23 = <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">{t21}{t22}</div>;
    $[101] = t21;
    $[102] = t22;
    $[103] = t23;
  } else {
    t23 = $[103];
  }
  let t24;
  if ($[104] !== t10 || $[105] !== t18 || $[106] !== t23) {
    t24 = <div className="w-full"><div data-view-transition-name="universal-chat-bubble" className="relative rounded-[28px] border border-border/25 bg-gradient-to-br from-primary/12 via-background/80 to-background/55 px-5 py-6 shadow-[030px_65px_-32px_rgba(22,27,45,0.85)] backdrop-blur-xl"><div className="flex flex-col gap-3">{t10}{t18}{t23}</div></div></div>;
    $[104] = t10;
    $[105] = t18;
    $[106] = t23;
    $[107] = t24;
  } else {
    t24 = $[107];
  }
  return t24;
}
function _temp(option) {
  return option.label;
}